/**
 * "Add new item to import" form for import processes (import_purchase /
 * import_return). Unlike export/transfer — which pick existing inventory — an
 * import DEFINES new items that get created when the process is approved. Picking
 * a model auto-fills item type / category / name (editable). Items can carry
 * explicit service tags (one per line) or a plain quantity.
 */
import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import {
  Button,
  Card,
  CardBody,
  Input,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useItemModels, useOwners, type ImportItemMetadata } from '@/api/capacities';
import { DateInput } from '@/components/date-input';
import { NumericInput } from '@/components/numeric-input';

const CONDITIONS = ['new', 'used', 'refurbished', 'damaged'];

interface Draft {
  model_id: string;
  item_type: 'equipment' | 'component';
  category: string;
  name: string;
  hasTags: boolean;
  serviceTags: string;
  quantity: string;
  condition: string;
  owner: string;
  purchase_price: string;
  purchase_date: string;
  warranty_start: string;
  warranty_end: string;
  description: string;
}

const emptyDraft: Draft = {
  model_id: '',
  item_type: 'equipment',
  category: '',
  name: '',
  hasTags: true,
  serviceTags: '',
  quantity: '1',
  condition: 'new',
  owner: '',
  purchase_price: '',
  purchase_date: '',
  warranty_start: '',
  warranty_end: '',
  description: '',
};

export function ImportItemForm({ onAdd }: { onAdd: (item: ImportItemMetadata) => void }) {
  const [d, setD] = useState<Draft>(emptyDraft);
  const { data: modelsRes } = useItemModels({ page_size: 500 });
  const { data: ownersRes } = useOwners();
  const models = modelsRes?.results ?? [];
  const owners = ownersRes?.results ?? [];

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((s) => ({ ...s, [k]: v }));

  const pickModel = (id: string) => {
    const m = models.find((x) => x.id === id);
    setD((s) => ({
      ...s,
      model_id: id,
      item_type: (m?.item_type as Draft['item_type']) ?? s.item_type,
      category: m?.category ?? s.category,
      name: m?.name ?? m?.model ?? s.name,
    }));
  };

  const tags = useMemo(
    () => d.serviceTags.split(/[\n,]/).map((t) => t.trim()).filter(Boolean),
    [d.serviceTags],
  );
  const canAdd = Boolean(d.name.trim() && d.category.trim() && (d.hasTags ? tags.length > 0 : Number(d.quantity) > 0));

  const add = () => {
    if (!canAdd) return;
    const item: ImportItemMetadata = {
      model_id: d.model_id || undefined,
      item_type: d.item_type,
      category: d.category.trim(),
      name: d.name.trim(),
      condition: d.condition,
      status: 'available',
      owner: d.owner || undefined,
      purchase_price: d.purchase_price ? Number(d.purchase_price) : undefined,
      purchase_date: d.purchase_date || undefined,
      warranty_start: d.warranty_start || undefined,
      warranty_end: d.warranty_end || undefined,
      description: d.description || undefined,
      ...(d.hasTags ? { service_tags: tags } : { quantity: Number(d.quantity) }),
    };
    onAdd(item);
    setD(emptyDraft);
  };

  const col: React.CSSProperties = { display: 'grid', gap: 'var(--vn-space-3)' };
  const row: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 'var(--vn-space-3)',
  };

  return (
    <Card flat>
      <CardBody style={col}>
      <div style={row}>
        <Field label="Model">
          {() => (
            <Select value={d.model_id} onValueChange={pickModel}>
              <SelectTrigger>
                <SelectValue placeholder="Select a model…" />
              </SelectTrigger>
              <SelectContent>
                {models.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name || m.model} · {m.category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Item type">
          {() => (
            <Select value={d.item_type} onValueChange={(v) => set('item_type', v as Draft['item_type'])}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="equipment">Equipment</SelectItem>
                <SelectItem value="component">Component</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
      </div>

      <div style={row}>
        <Field label="Name">
          {(p) => <Input {...p} value={d.name} onChange={(e) => set('name', e.target.value)} placeholder="Item name" />}
        </Field>
        <Field label="Category">
          {(p) => <Input {...p} value={d.category} onChange={(e) => set('category', e.target.value)} placeholder="server, ram…" />}
        </Field>
      </div>

      <div style={row}>
        <Field label="Quantity type">
          {() => (
            <Select value={d.hasTags ? 'tags' : 'qty'} onValueChange={(v) => set('hasTags', v === 'tags')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tags">Service tags / serials</SelectItem>
                <SelectItem value="qty">Plain quantity</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
        {d.hasTags ? (
          <Field label="Service tags (one per line)">
            {(p) => (
              <Textarea
                {...p}
                rows={2}
                value={d.serviceTags}
                onChange={(e) => set('serviceTags', e.target.value)}
                placeholder="SN001&#10;SN002"
              />
            )}
          </Field>
        ) : (
          <Field label="Quantity">
            {(p) => <NumericInput {...p} min={1} value={d.quantity} onChange={(e) => set('quantity', e.target.value)} />}
          </Field>
        )}
      </div>

      <div style={row}>
        <Field label="Condition">
          {() => (
            <Select value={d.condition} onValueChange={(v) => set('condition', v)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONDITIONS.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Owner">
          {() => (
            <Select value={d.owner} onValueChange={(v) => set('owner', v)}>
              <SelectTrigger>
                <SelectValue placeholder="Select owner…" />
              </SelectTrigger>
              <SelectContent>
                {owners.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Purchase price">
          {(p) => <NumericInput {...p} min={0} value={d.purchase_price} onChange={(e) => set('purchase_price', e.target.value)} />}
        </Field>
      </div>

      <div style={row}>
        <Field label="Purchase date">
          {(p) => <DateInput {...p} value={d.purchase_date} onChange={(e) => set('purchase_date', e.target.value)} />}
        </Field>
        <Field label="Warranty start">
          {(p) => <DateInput {...p} value={d.warranty_start} onChange={(e) => set('warranty_start', e.target.value)} />}
        </Field>
        <Field label="Warranty end">
          {(p) => <DateInput {...p} value={d.warranty_end} onChange={(e) => set('warranty_end', e.target.value)} />}
        </Field>
      </div>

      <Field label="Description">
        {(p) => <Textarea {...p} rows={2} value={d.description} onChange={(e) => set('description', e.target.value)} />}
      </Field>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="secondary" startIcon={<Plus size={14} />} disabled={!canAdd} onClick={add}>
          Add item to import
        </Button>
      </div>
      </CardBody>
    </Card>
  );
}
